import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { Link } from 'react-router-dom';
import Select from "react-select";
import PageTitle from "../../components/PageTitle";
import { CHARACTERS } from "./characterData";
import { charOptions, charSortOptions } from "./characterOptions";
import { useTagSearch } from "../../hooks/useTagSearch";
import "./character.css";

const Character = () => {
  const {
    displayTags,
    handleChange,
    currentTags,
    searchQuery,
    handleSearchChange,
    optionsWithCounts
  } = useTagSearch(charOptions, CHARACTERS);

  const [sortType, setSortType] = useState("titleAsc");
  const [inputValue, setInputValue] = useState(searchQuery);

  // フィルタリング処理
  const filteredCharacters = CHARACTERS.filter(char => {
    const query = searchQuery.toLowerCase();
    const matchSearch = searchQuery === "" ||
      char.name.toLowerCase().includes(query) ||
      char.ruby.toLowerCase().includes(query) ||
      (char.description || "").toLowerCase().includes(query) ||
      (char.tags || []).some(tag => tag.toLowerCase().includes(query));

    const matchTags = currentTags.length === 0 ||
      currentTags.every(tag => (char.tags || []).includes(tag));

    return matchSearch && matchTags;
  });

  // ソート処理
  const sortedCharacters = [...filteredCharacters].sort((a, b) => {
    if (sortType === 'titleAsc') {
      return a.name.localeCompare(b.name, 'ja');
    }
    if (sortType === 'titleDesc') {
      return b.name.localeCompare(a.name, 'ja');
    }
    if (sortType === 'debutAsc') {
      return (a.debut || "").localeCompare(b.debut || "");
    }
    if (sortType === 'debutDesc') {
      return (b.debut || "").localeCompare(a.debut || "");
    }
    return 0;
  });

  // 検索入力の同期
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  // デバウンス検索
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue !== searchQuery) {
        handleSearchChange({ target: { value: inputValue } } as any);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [inputValue, searchQuery, handleSearchChange]);

  return (
    <div className="characters-page">
      <div className="profile-page-wrapper"></div>
      <PageTitle title={"Character"} />

      <h1 className="top-title">Character</h1>

      <div className="Searchflame">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>

          <select
            className='SortMenu'
            value={sortType}
            onChange={(e) => setSortType(e.target.value)}
          >
            {charSortOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <input
            className="QuickSearchMenu"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="キーワード検索..."
          />

          <div className="TagSearchMenu" style={{ width: '100%' }}>
            <Select
              isMulti
              options={optionsWithCounts}
              value={displayTags}
              onChange={handleChange}
              placeholder="ソート / フィルタ"
              styles={{
                control: (base) => ({
                  ...base,
                  width: '100%',
                  minHeight: '40px',
                  borderRadius: '10px',
                  border: '1px solid #ccc',
                  fontSize: '18px',
                }),
                placeholder: (base) => ({ ...base, textAlign: 'center' }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 })
              }}
              menuPortalTarget={document.body}
            />
          </div>
        </div>
      </div>

      <div className="main">
        <div className="character-grid">
          <AnimatePresence mode='wait'>
            {sortedCharacters.length > 0 ? (
              sortedCharacters.map((char) => (
                <motion.div
                  key={char.id}
                  className="char-grid-item"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <Link 
                    to={`/character/${char.id}`} 
                    state={{ fromMenu: true }}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <motion.div className="char-tile-container" whileHover={{ y: 5 }}>
                      <div className="char-image-square">
                        <img src={char.image} alt={char.name} className="char-tile-img" />
                      </div>
                      <div className="char-tile-info">
                        <span className="char-tile-ruby">{char.ruby}</span>
                        <h3 className="char-tile-name">{char.name}</h3>
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              ))
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ color: 'white', gridColumn: '1/-1', textAlign: 'center', padding: '5rem', opacity: 0.8 }}
              >
                該当するキャラクターが見つかりませんでした。
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Character;
